import { useState, useEffect, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Loader2, Check, AlertCircle, Trophy, AlertTriangle } from 'lucide-react'
import { matchService, formatScore } from '@/services/matchService'
import type { ScoreSet, OpponentGamesCount } from '@/services/matchService'
import { BadmintonScoreEntry } from './BadmintonSetScore'
import type { BracketPlayer } from '@/services/bracketPlayerService'
import type { MatchFormat } from '@/services/leagueService'

type SeasonStatus = 'upcoming' | 'active' | 'completed'

interface RecordGameDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  bracketId: string
  players: BracketPlayer[]
  currentUserId?: string
  /** If true, allows selecting both players (admin mode from admin panel) */
  adminSelectMode?: boolean
  /** Season status - used to prevent recording games for inactive seasons */
  seasonStatus?: SeasonStatus
  /** Season name - shown in error message */
  seasonName?: string
  /** Match format - determines number of sets (default: bestOf1) */
  matchFormat?: MatchFormat
  /** If true, match will be auto-approved (due to admin privilege or league setting) */
  autoApprove?: boolean
  /** Maximum games allowed per opponent per season (null = unlimited) */
  maxGamesPerOpponent?: number | null
  onSuccess?: () => void
}

function getInitials(name: string) {
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
}

// Helper: get number of sets based on match format
function getRequiredSets(matchFormat: MatchFormat): { min: number; max: number; toWin: number } {
  switch (matchFormat) {
    case 'bestOf5': return { min: 3, max: 5, toWin: 3 }
    case 'bestOf3': return { min: 2, max: 3, toWin: 2 }
    case 'bestOf1':
    default: return { min: 1, max: 1, toWin: 1 }
  }
}

// Helper: get match format label
function getMatchFormatLabel(matchFormat: MatchFormat): string {
  switch (matchFormat) {
    case 'bestOf5': return 'Best of 5'
    case 'bestOf3': return 'Best of 3'
    case 'bestOf1':
    default: return 'Single Game'
  }
}

// Badminton scoring rules validation
// - 21 points to win (with 2 point gap)
// - If 20-20, play continues until 2 point gap
// - Maximum 30 points (30-28 from 28-28, or 30-29 sudden death from 29-29)
function validateBadmintonScore(score1: number, score2: number): { valid: boolean; error?: string } {
  const higher = Math.max(score1, score2)
  const lower = Math.min(score1, score2)
  const gap = higher - lower

  // Both scores must be positive
  if (score1 < 0 || score2 < 0) {
    return { valid: false, error: 'Scores must be positive' }
  }

  // Can't have a tie
  if (score1 === score2) {
    return { valid: false, error: 'Scores cannot be tied' }
  }

  // Maximum score is 30
  if (higher > 30) {
    return { valid: false, error: 'Maximum score is 30' }
  }

  // Standard win: 21 with 2+ point gap
  if (higher === 21 && gap >= 2) {
    return { valid: true }
  }

  // Deuce win: 22-29 with exactly 2 point gap
  if (higher >= 22 && higher <= 29 && gap === 2) {
    return { valid: true }
  }

  // Maximum score win: 30-28 (from 28-28) or 30-29 (sudden death from 29-29)
  if (higher === 30 && (lower === 28 || lower === 29)) {
    return { valid: true }
  }

  // Invalid score
  if (higher < 21) {
    return { valid: false, error: 'Winner must reach at least 21 points' }
  }

  if (higher === 21 && gap < 2) {
    return { valid: false, error: 'Need 2 point gap to win (e.g., 21-19)' }
  }

  if (higher >= 22 && higher < 30 && gap !== 2) {
    return { valid: false, error: 'After 20-20, need exactly 2 point gap to win' }
  }

  if (higher === 30 && lower !== 28 && lower !== 29) {
    return { valid: false, error: 'At 30 points, loser must be 28 or 29' }
  }

  return { valid: false, error: 'Invalid badminton score' }
}

// Validate all sets
function validateAllSets(sets: ScoreSet[]): { valid: boolean; errors: string[] } {
  const errors: string[] = []
  
  for (let i = 0; i < sets.length; i++) {
    const result = validateBadmintonScore(sets[i].player1Score, sets[i].player2Score)
    if (!result.valid && result.error) {
      errors.push(`Game ${i + 1}: ${result.error}`)
    }
  }
  
  return { valid: errors.length === 0, errors }
}

export function RecordGameDialog({
  open,
  onOpenChange,
  bracketId,
  players,
  currentUserId,
  adminSelectMode = false,
  seasonStatus = 'active',
  seasonName,
  matchFormat = 'bestOf1',
  autoApprove = false,
  maxGamesPerOpponent,
  onSuccess,
}: RecordGameDialogProps) {
  const [step, setStep] = useState<'players' | 'score' | 'confirm'>('players')
  const [player1Id, setPlayer1Id] = useState<string | null>(null)
  const [player2Id, setPlayer2Id] = useState<string | null>(null)
  
  // Get required sets based on match format
  const { min: minSets, max: maxSets, toWin: setsToWin } = getRequiredSets(matchFormat)
  
  // Initialize sets based on match format
  const [sets, setSets] = useState<ScoreSet[]>(() => 
    Array.from({ length: minSets }, () => ({ player1Score: 0, player2Score: 0 }))
  )
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // Opponent games count state
  const [opponentGamesCount, setOpponentGamesCount] = useState<OpponentGamesCount | null>(null)
  const [loadingGamesCount, setLoadingGamesCount] = useState(false)

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setStep('players')
      // Always pre-select current user as player1 unless in admin select mode
      if (!adminSelectMode && currentUserId && players.some(p => p.userId === currentUserId)) {
        setPlayer1Id(currentUserId)
      } else {
        setPlayer1Id(null)
      }
      setPlayer2Id(null)
      // Initialize with min sets for the match format
      setSets(Array.from({ length: minSets }, () => ({ player1Score: 0, player2Score: 0 })))
      setError(null)
      setOpponentGamesCount(null)
    }
  }, [open, adminSelectMode, currentUserId, players, minSets])

  // Fetch games count when opponent changes (only if there's a limit)
  const fetchOpponentGamesCount = useCallback(async (opponentId: string) => {
    if (!maxGamesPerOpponent) {
      setOpponentGamesCount(null)
      return
    }
    
    setLoadingGamesCount(true)
    try {
      const result = await matchService.getOpponentGamesCount(bracketId, opponentId)
      setOpponentGamesCount(result)
    } catch (err) {
      console.error('Failed to fetch games count:', err)
      setOpponentGamesCount(null)
    } finally {
      setLoadingGamesCount(false)
    }
  }, [bracketId, maxGamesPerOpponent])

  // Trigger fetch when player2 changes
  useEffect(() => {
    if (player2Id && maxGamesPerOpponent) {
      fetchOpponentGamesCount(player2Id)
    } else {
      setOpponentGamesCount(null)
    }
  }, [player2Id, maxGamesPerOpponent, fetchOpponentGamesCount])

  const player1 = players.find(p => p.userId === player1Id)
  const player2 = players.find(p => p.userId === player2Id)

  // Available opponents (exclude selected player1)
  const availableOpponents = players.filter(p => p.userId !== player1Id)

  // In admin select mode, show all other players. Otherwise, only show other players (not yourself)
  const opponentOptions = adminSelectMode 
    ? availableOpponents 
    : availableOpponents.filter(p => p.userId !== currentUserId)

  const handleSetsChange = (newSets: ScoreSet[]) => {
    setSets(newSets)
  }

  // Calculate wins for each player
  const player1Wins = sets.filter(s => s.player1Score > s.player2Score).length
  const player2Wins = sets.filter(s => s.player2Score > s.player1Score).length

  // Determine winner based on match format
  const winner = player1Wins >= setsToWin ? 'player1' : player2Wins >= setsToWin ? 'player2' : null

  // Check if we can proceed - need both players selected and games remaining (if limit applies)
  const hasGamesRemaining = !maxGamesPerOpponent || 
    loadingGamesCount || 
    opponentGamesCount?.remaining == null || 
    opponentGamesCount.remaining > 0
  const canProceedToScore = player1Id && player2Id && hasGamesRemaining && !loadingGamesCount
  
  // Validate all sets with badminton rules
  const setsValidation = validateAllSets(sets)
  
  // Need all sets to be valid and have a winner (reached required wins)
  const canProceedToConfirm = setsValidation.valid && winner

  const handleSubmit = async () => {
    if (!player1Id || !player2Id) return

    setSubmitting(true)
    setError(null)

    try {
      await matchService.submit(bracketId, {
        player1Id,
        player2Id,
        sets,
      })
      
      onOpenChange(false)
      onSuccess?.()
    } catch (err) {
      setError((err as Error)?.message || 'Failed to submit match')
    } finally {
      setSubmitting(false)
    }
  }

  // Check if season is active
  const isSeasonActive = seasonStatus === 'active'
  const seasonStatusMessage = seasonStatus === 'upcoming' 
    ? `Season "${seasonName || 'this season'}" hasn't started yet`
    : seasonStatus === 'completed'
      ? `Season "${seasonName || 'this season'}" has ended`
      : null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-primary" />
            Record a Game
          </DialogTitle>
          <DialogDescription>
            {!isSeasonActive 
              ? 'This season is not currently active'
              : step === 'players' && `Select the players who played this match (${getMatchFormatLabel(matchFormat)})`
            }
            {isSeasonActive && step === 'score' && (
              matchFormat === 'bestOf1' 
                ? 'Enter the final score'
                : `Enter the score for each game (first to ${setsToWin} wins)`
            )}
            {isSeasonActive && step === 'confirm' && 'Review and submit the match result'}
          </DialogDescription>
        </DialogHeader>

        {/* Season not active warning */}
        {!isSeasonActive && (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium mb-2">Cannot Record Game</p>
            <p className="text-sm text-muted-foreground mb-4">
              {seasonStatusMessage}
            </p>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </div>
        )}

        {isSeasonActive && <div className="space-y-4 pt-2">
          {/* Step 1: Select Players */}
          {step === 'players' && (
            <>
              {/* Player 1 Selection */}
              <div className="space-y-2">
                <Label>{adminSelectMode ? 'Player 1' : 'You'}</Label>
                {adminSelectMode ? (
                  <div className="grid gap-1 max-h-32 overflow-y-auto">
                    {players.map(player => (
                      <button
                        key={player.userId}
                        type="button"
                        onClick={() => {
                          setPlayer1Id(player.userId)
                          if (player2Id === player.userId) setPlayer2Id(null)
                        }}
                        className={`flex items-center gap-3 p-2 rounded-lg border transition-colors text-left ${
                          player1Id === player.userId
                            ? 'border-primary bg-primary/5'
                            : 'hover:bg-muted/50'
                        }`}
                      >
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={player.avatar} alt={player.displayName} />
                          <AvatarFallback className="text-xs">{getInitials(player.displayName)}</AvatarFallback>
                        </Avatar>
                        <span className="font-medium text-sm">{player.displayName}</span>
                        {player1Id === player.userId && (
                          <Check className="h-4 w-4 text-primary ml-auto" />
                        )}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={player1?.avatar} alt={player1?.displayName} />
                      <AvatarFallback>{player1 ? getInitials(player1.displayName) : '?'}</AvatarFallback>
                    </Avatar>
                    <span className="font-medium">{player1?.displayName || 'You'}</span>
                  </div>
                )}
              </div>

              {/* Player 2 Selection */}
              <div className="space-y-2">
                <Label>Opponent</Label>
                <div className="grid gap-1 max-h-40 overflow-y-auto">
                  {opponentOptions.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No opponents available
                    </p>
                  ) : (
                    opponentOptions.map(player => (
                      <button
                        key={player.userId}
                        type="button"
                        onClick={() => setPlayer2Id(player.userId)}
                        className={`flex items-center gap-3 p-2 rounded-lg border transition-colors text-left ${
                          player2Id === player.userId
                            ? 'border-primary bg-primary/5'
                            : 'hover:bg-muted/50'
                        }`}
                      >
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={player.avatar} alt={player.displayName} />
                          <AvatarFallback className="text-xs">{getInitials(player.displayName)}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <span className="font-medium text-sm">{player.displayName}</span>
                          <span className="text-xs text-muted-foreground ml-2">#{player.ranking}</span>
                        </div>
                        {player2Id === player.userId && (
                          <Check className="h-4 w-4 text-primary" />
                        )}
                      </button>
                    ))
                  )}
                </div>
              </div>

              {/* Games remaining indicator */}
              {player2Id && maxGamesPerOpponent && (
                <div className={`flex items-center gap-2 p-2 rounded-lg text-sm ${
                  loadingGamesCount
                    ? 'bg-muted/50'
                    : opponentGamesCount?.remaining === 0
                      ? 'bg-destructive/10 border border-destructive/20 text-destructive'
                      : opponentGamesCount?.remaining === 1
                        ? 'bg-amber-500/10 border border-amber-500/20 text-amber-700 dark:text-amber-400'
                        : 'bg-muted/50 text-muted-foreground'
                }`}>
                  {loadingGamesCount ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Checking game limit...</span>
                    </>
                  ) : opponentGamesCount?.remaining === 0 ? (
                    <>
                      <AlertTriangle className="h-4 w-4" />
                      <span>No games remaining against this opponent this season</span>
                    </>
                  ) : opponentGamesCount?.remaining != null ? (
                    <>
                      <Trophy className="h-4 w-4" />
                      <span>
                        {opponentGamesCount.remaining} of {opponentGamesCount.maxGames} game{opponentGamesCount.remaining !== 1 ? 's' : ''} remaining this season
                      </span>
                    </>
                  ) : null}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button onClick={() => setStep('score')} disabled={!canProceedToScore}>
                  Next: Enter Score
                </Button>
              </div>
            </>
          )}

          {/* Step 2: Enter Score */}
          {step === 'score' && player1 && player2 && (
            <>
              {/* Score Entry */}
              <BadmintonScoreEntry
                player1={{ displayName: player1.displayName, avatar: player1.avatar }}
                player2={{ displayName: player2.displayName, avatar: player2.avatar }}
                sets={sets}
                onSetsChange={handleSetsChange}
                minSets={minSets}
                maxSets={maxSets}
                setsToWin={setsToWin}
              />

              {/* Validation errors */}
              {!setsValidation.valid && setsValidation.errors.length > 0 && (
                <div className="text-sm text-amber-600 text-center space-y-1">
                  {setsValidation.errors.map((error, i) => (
                    <p key={i}>{error}</p>
                  ))}
                </div>
              )}
              
              {/* Scoring hint */}
              <p className="text-xs text-muted-foreground text-center">
                Tap a player to select the winner • Adjust scores with arrows
              </p>

              <div className="flex justify-between gap-2 pt-2">
                <Button variant="outline" onClick={() => setStep('players')}>
                  Back
                </Button>
                <Button onClick={() => setStep('confirm')} disabled={!canProceedToConfirm}>
                  Review Match
                </Button>
              </div>
            </>
          )}

          {/* Step 3: Confirm */}
          {step === 'confirm' && (
            <>
              <div className="rounded-lg border p-4 space-y-4">
                {/* Match Summary */}
                <div className="grid grid-cols-5 gap-2 text-center">
                  <div className="col-span-2">
                    <Avatar className={`h-14 w-14 mx-auto mb-1 ${winner === 'player1' ? 'ring-2 ring-green-500 ring-offset-2' : ''}`}>
                      <AvatarImage src={player1?.avatar} alt={player1?.displayName} />
                      <AvatarFallback>{player1 ? getInitials(player1.displayName) : ''}</AvatarFallback>
                    </Avatar>
                    <p className={`text-sm font-medium ${winner === 'player1' ? 'text-green-600 dark:text-green-400' : ''}`}>
                      {player1?.displayName}
                    </p>
                    {winner === 'player1' && (
                      <span className="text-xs bg-green-500/10 text-green-600 px-2 py-0.5 rounded-full">
                        Winner
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col items-center justify-center">
                    <span className="text-2xl font-bold">{formatScore(sets)}</span>
                  </div>
                  <div className="col-span-2">
                    <Avatar className={`h-14 w-14 mx-auto mb-1 ${winner === 'player2' ? 'ring-2 ring-green-500 ring-offset-2' : ''}`}>
                      <AvatarImage src={player2?.avatar} alt={player2?.displayName} />
                      <AvatarFallback>{player2 ? getInitials(player2.displayName) : ''}</AvatarFallback>
                    </Avatar>
                    <p className={`text-sm font-medium ${winner === 'player2' ? 'text-green-600 dark:text-green-400' : ''}`}>
                      {player2?.displayName}
                    </p>
                    {winner === 'player2' && (
                      <span className="text-xs bg-green-500/10 text-green-600 px-2 py-0.5 rounded-full">
                        Winner
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Status Message */}
              {autoApprove ? (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                  <Check className="h-4 w-4 text-green-600" />
                  <span className="text-sm text-green-700 dark:text-green-400">
                    This match will be automatically approved.
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                  <AlertCircle className="h-4 w-4 text-amber-600" />
                  <span className="text-sm text-amber-700 dark:text-amber-400">
                    {player2?.displayName} will be notified to confirm this result.
                  </span>
                </div>
              )}

              {error && (
                <p className="text-sm text-destructive text-center">{error}</p>
              )}

              <div className="flex justify-between gap-2 pt-2">
                <Button variant="outline" onClick={() => setStep('score')}>
                  Back
                </Button>
                <Button onClick={handleSubmit} disabled={submitting}>
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Match'
                  )}
                </Button>
              </div>
            </>
          )}
        </div>}
      </DialogContent>
    </Dialog>
  )
}

